import { ApiService } from '../ApiService';
import { type NearbyFilterOption } from '@/constants/lists';
import { Member, MembersView } from '~/models/Member';
import { MemberAccount } from '~/models/MemberAccount';
import { MemberDetails } from '~/models/MemberDetails';
import { PostsView } from '~/models/Post';
import { PhotosView } from '~/models/Photo';
import { plainToClass } from '~/models/transform';
import { BaseListRequestParams } from '~/models/BaseRequestParams';
import { ISearchCardV6 } from '~/models/SearchCardV6';

// Parameter Interfaces
export interface OnlineMembersParams extends BaseListRequestParams {
  includeUnfilteredItemCount?: boolean;
}

export interface VisitorMembersParams extends BaseListRequestParams {
  includeUnfilteredItemCount?: boolean;
}

export interface FollowersMembersParams extends BaseListRequestParams {
  memberId?: number;
  myTypeFilter?: boolean;
  sortByDistance?: boolean;
}

export interface FollowingMembersParams extends BaseListRequestParams {
  memberId?: number;
  sortByDistance?: boolean;
}

export interface NearbyMembersParams extends BaseListRequestParams {
  option?: NearbyFilterOption;
  lat?: number;
  lng?: number;
}

export interface MemberAccountParams extends BaseListRequestParams {
  withPhotos?: boolean;
}

export interface MemberPhotosParams extends BaseListRequestParams {
  withDetails?: boolean;
}

export interface BlockedMembersParams extends BaseListRequestParams {}

export class MembersService {
  static async getOnlineMembers(params?: OnlineMembersParams): Promise<MembersView> {
    const response = await ApiService.get<MembersView>('members/online', params);
    return plainToClass(MembersView, response);
  }

  static async getNearbyMembers(params?: NearbyMembersParams): Promise<MembersView> {
    const { option, ...restParams } = params || {};
    const endpointOption = option === 'all' ? undefined : option;
    
    // Build endpoint with option in path (omit "all" to use the backend's default nearby list).
    const endpoint = endpointOption ? `members/nearby/${endpointOption}` : 'members/nearby';
    
    const response = await ApiService.get<MembersView>(endpoint, restParams);
    return plainToClass(MembersView, response);
  }

  static async getFollowingMembers(params?: FollowingMembersParams): Promise<MembersView> {
    const endpoint = params?.memberId ? `members/${params.memberId}/following` : 'members/following';
    const { memberId, ...restParams } = params || {}; // Remove memberId from params sent to API
    const response = await ApiService.get<MembersView>(endpoint, restParams);
    return plainToClass(MembersView, response);
  }

  static async getFollowersMembers(params?: FollowersMembersParams): Promise<MembersView> {
    const endpoint = params?.memberId ? `members/${params.memberId}/followers` : 'members/followers';
    const { memberId, ...restParams } = params || {}; // Remove memberId from params sent to API
    const response = await ApiService.get<MembersView>(endpoint, restParams);
    return plainToClass(MembersView, response);
  }

  static async getBlockedMembers(params?: BlockedMembersParams): Promise<MembersView> {
    const response = await ApiService.get<MembersView>('members/blocked', params);
    return plainToClass(MembersView, response);
  }

  /**
   * Fetches members who currently have access to my private photos (authorizations I granted).
   * @param params Optional pagination parameters
   */
  static async getUnlockedMembers(params?: BaseListRequestParams): Promise<MembersView> {
    const response = await ApiService.get<MembersView>('members/unlocked', params);
    return plainToClass(MembersView, response);
  }

  static async getNewMembers(params?: BaseListRequestParams): Promise<MembersView> {
    const response = await ApiService.get<MembersView>('members/new', params);
    return plainToClass(MembersView, response);
  }

  static async getBirthdayMembers(params?: BaseListRequestParams): Promise<MembersView> {
    const response = await ApiService.get<MembersView>('members/birthdays', params);
    return plainToClass(MembersView, response);
  }

  static async getVisitorMembers(params?: VisitorMembersParams): Promise<MembersView> {
    const response = await ApiService.get<MembersView>('members/visitors', params);
    return plainToClass(MembersView, response);
  }

  static async getMemberAccount(memberId: number, params?: MemberAccountParams): Promise<MemberAccount> {
    const endpoint = `members/${memberId}/account`;
    const response = await ApiService.get<MemberAccount>(endpoint, params);
    return plainToClass(MemberAccount, response);
  }

  /**
   * Fetches detailed member information by ID.
   * @param id Member ID (number)
   */
  static async getMemberDetailsById(id: number): Promise<MemberDetails> {
    const endpoint = `members/${id}`;
    const response = await ApiService.get<MemberDetails>(endpoint);
    return plainToClass(MemberDetails, response);
  }

  /**
   * Fetches detailed member information by username.
   * @param username Member username (string)
   */
  static async getMemberDetailsByUsername(username: string): Promise<MemberDetails> {
    const endpoint = `members/${username}/details`;
    const response = await ApiService.get<MemberDetails>(endpoint);
    return plainToClass(MemberDetails, response);
  }


  static async getMemberWall(memberId: number, params?: BaseListRequestParams): Promise<PostsView> {
    const endpoint = `members/${memberId}/wall`;
    const response = await ApiService.get<PostsView>(endpoint, params);
    return plainToClass(PostsView, response);
  }

  /**
   * Creates a new post on a member's wall.
   * @param memberId The ID of the member whose wall to post on
   * @param text The post text
   */
  static async createPostOnMemberWall(memberId: number, text: string): Promise<void> {
    const endpoint = `members/${memberId}/post`;
    await ApiService.post(endpoint, { Text: text });
  }

  static async getMemberPublicPhotos(memberId: number, params?: MemberPhotosParams): Promise<PhotosView> {
    const endpoint = `members/${memberId}/photos/public`;
    const response = await ApiService.get<PhotosView>(endpoint, params);
    return plainToClass(PhotosView, response);
  }

  static async getMemberPrivatePhotos(memberId: number, params?: MemberPhotosParams): Promise<PhotosView> {
    const endpoint = `members/${memberId}/photos/private`;
    const response = await ApiService.get<PhotosView>(endpoint, params);
    return plainToClass(PhotosView, response);
  }

  static async followMember(memberId: number): Promise<any> {
    const endpoint = `members/${memberId}/follow`;
    return ApiService.post(endpoint, {});
  }

  static async unfollowMember(memberId: number): Promise<any> {
    const endpoint = `members/${memberId}/unfollow`;
    return ApiService.post(endpoint, {});
  }

  static async blockMember(memberId: number): Promise<any> {
    const endpoint = `members/${memberId}/block`;
    return ApiService.post(endpoint, {});
  }

  static async unblockMember(memberId: number): Promise<any> {
    const endpoint = `members/${memberId}/unblock`;
    return ApiService.post(endpoint, {});
  }

  /**
   * Gets the last message ID with a specific member
   * @param memberId The ID of the member to get last message with
   * @returns Promise with the last message ID
   */
  static async getLastMessageId(memberId: number): Promise<{ Id: number }> {
    const endpoint = `members/${memberId}/last-message-id`;
    const response = await ApiService.get<{ Id: number }>(endpoint);
    return response;
  }

  /**
   * Creates a new message with a member and returns the last message ID
   * @param memberId The ID of the member to send message to
   * @param text The message text
   * @returns Promise with the last message ID
   */
  static async createNewMessage(memberId: number, text: string): Promise<{ Id: number }> {
    const endpoint = `members/${memberId}/message`;
    const conversationItem = { Text: text };
    const response = await ApiService.post<{ Id: number }>(endpoint, conversationItem);
    return response;
  }

  /**
   * Requests access to a member's private photos.
   * @param memberId The ID of the member whose private photos are being requested
   */
  static async requestPhotoUnlock(memberId: number): Promise<void> {
    const endpoint = `members/${memberId}/unlock/request`;
    return ApiService.post(endpoint, {});
  }

  /**
   * Revokes access to the current user's private photos for the specified member.
   * Used when the current user is the photo owner removing a previously-granted authorization.
   * @param memberId The ID of the member whose access should be revoked
   */
  static async removePhotoUnlock(memberId: number): Promise<void> {
    const endpoint = `members/${memberId}/unlock/remove`;
    return ApiService.post(endpoint, {});
  }

  /**
   * Forfeits the current user's own access to another member's private photos.
   * Used when the current user no longer wants access to a member's private photos
   * that the member previously granted them.
   * @param memberId The ID of the member whose private photos access is being forfeited
   */
  static async forfeitPhotoUnlock(memberId: number): Promise<void> {
    const endpoint = `members/${memberId}/unlock/forfeit`;
    return ApiService.post(endpoint, {});
  }

  /**
   * Searches for members based on search criteria.
   * @param searchCard Search criteria filters
   * @param params Optional pagination parameters
   */
  static async searchMembers(
    searchCard: ISearchCardV6,
    params?: BaseListRequestParams
  ): Promise<MembersView> {
    const response = await ApiService.request<MembersView>('POST', 'members/search', searchCard, params);
    return plainToClass(MembersView, response);
  }

  /**
   * Fetches available search options with all possible values for each filter field.
   */
  static async getSearchOptions(): Promise<ISearchCardV6> {
    return ApiService.get<ISearchCardV6>('members/search/options');
  }
}
